import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '@root/app-state.module';
import { ReportHeader, ReportsState } from '@store/reports/reports.model';
import { PropertyInformation } from '@store/search-board/search-board.model';
import { ButtonToggles } from '@root/reports/shared/models/button-toggles';
import { PreferenceCode, PreferenceGroup } from '@store/user/user.model';
import {
  selectClip,
  selectSelectedPropertyInformation,
  selectSubHeaderHeader
} from '@store/reports/reports.selector';
import { selectSavedProperties } from '@store/saved-properties/saved-properties.selector';
import { selectPreferenceGroupByCode } from '@store/user/user.selector';
import { selectSubHeaderButtons } from '@store/reports/reports.selector';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

// ─── localStorage keys ──────────────────────────────────────────────
export const SUBHEADER_MOCK_STORAGE_KEY = 'subheader_mock';
export const SUBHEADER_MOCK_ENABLED_KEY = 'subheader_mock_enabled';

// ─── Snapshot shape ─────────────────────────────────────────────────
export interface SubheaderSnapshot {
  propertyInformation: PropertyInformation;
  clip: string;
  buttons: ButtonToggles;
  header: ReportHeader;
  savedProperties: Array<PropertyInformation>;
  preferenceGroups: {
    hideForeclosureFlags?: PreferenceGroup;
    reportsReportConfig?: PreferenceGroup;
  };
}

// All keys the template accesses directly — must never be undefined
const DEFAULT_BUTTONS: ButtonToggles = {
  quickPrint:           { show: false },
  customizePrint:       { show: false },
  quickEmail:           { show: false },
  customizeEmail:       { show: false },
  customizeView:        { show: false },
  searchCriteriaView:   { show: false },
  transactionDesk:      { show: false },
  valueMap:             { show: false },
  comparablesGenerate:  { show: false },
  comparablesEdit:      { show: false },
};

// ─── Toggle helpers (used by meta-reducer & console commands) ───────
export function isSubheaderMockEnabled(): boolean {
  return true;
}

// ─── State override reader (called once at store boot) ──────────────
export function getSubheaderMockStateOverride(): Partial<AppState> | null {
  if (!isSubheaderMockEnabled()) {
    console.log('[SubheaderMock] ⏭️ Skipped — mock disabled');
    return null;
  }
  try {
    const raw = localStorage.getItem(SUBHEADER_MOCK_STORAGE_KEY);
    if (!raw) {
      console.log('[SubheaderMock] ⏭️ Skipped — no snapshot in localStorage yet. Navigate to a property report first to capture one.');
      return null;
    }
    const snap: SubheaderSnapshot = JSON.parse(raw);
    if (!snap?.propertyInformation) {
      console.log('[SubheaderMock] ⚠️ Snapshot found but has no propertyInformation — clearing and recapturing.');
      localStorage.removeItem(SUBHEADER_MOCK_STORAGE_KEY);
      return null;
    }
    console.log('[SubheaderMock] ✅ Hydrating store from snapshot:', snap.header?.address ?? '(no address yet)');
    return buildStateOverride(snap);
  } catch (e) {
    console.warn('[SubheaderMock] ❌ Failed to parse snapshot:', e);
    return null;
  }
}

function buildStateOverride(snap: SubheaderSnapshot): Partial<AppState> | null {
  // buttons must always be an object so the mocked subheader renders every toggle
  const reportsOverride: Partial<ReportsState> = {
    selectedPropertyInformation: snap.propertyInformation,
    clip: snap.clip ?? '',
    subheader: {
      header: snap.header ?? null,
      buttons: { ...DEFAULT_BUTTONS, ...snap.buttons },
    },
  };

  const result: any = {
    reports: reportsOverride,
  };

  // Seed savedProperties so the star icon & list don't crash
  if (snap.savedProperties) {
    result.savedProperties = {
      properties: snap.savedProperties,
      checkedProperties: [],
    };
  }

  return result;
}

// ─── Service: captures live store data → localStorage ───────────────
@Injectable({ providedIn: 'root' })
export class SubheaderMockService implements OnDestroy {
  private readonly sub = new Subscription();

  constructor(private readonly store$: Store<AppState>) {
    this.exposeConsoleControls();
    if (isSubheaderMockEnabled()) {
      this.startCapturing();
    }
  }

  // ── Capture live store values into localStorage ───────────────────
  private startCapturing(): void {
    let snapshot: Partial<SubheaderSnapshot> = this.loadSnapshot() ?? {};

    this.sub.add(
      this.store$.select(selectSelectedPropertyInformation)
        .pipe(filter(Boolean))
        .subscribe((propertyInformation: PropertyInformation) => {
          snapshot = { ...snapshot, propertyInformation };
          this.save(snapshot);
        })
    );

    this.sub.add(
      this.store$.select(selectSubHeaderHeader)
        .pipe(filter(Boolean))
        .subscribe((header: ReportHeader) => {
          snapshot = { ...snapshot, header };
          this.save(snapshot);
        })
    );

    this.sub.add(
      this.store$.select(selectClip)
        .pipe(filter(Boolean))
        .subscribe((clip: string) => {
          snapshot = { ...snapshot, clip };
          this.save(snapshot);
        })
    );

    this.sub.add(
      this.store$.select(selectSubHeaderButtons)
        .pipe(filter(Boolean))
        .subscribe((buttons: ButtonToggles) => {
          snapshot = { ...snapshot, buttons };
          this.save(snapshot);
        })
    );

    this.sub.add(
      this.store$.select(selectSavedProperties)
        .subscribe((properties: Array<PropertyInformation>) => {
          snapshot = { ...snapshot, savedProperties: properties ?? [] };
          this.save(snapshot);
        })
    );

    // Capture the two preference groups the subheader's combineLatest needs
    this.sub.add(
      this.store$.select(selectPreferenceGroupByCode(PreferenceCode.PG_HIDE_FORECLOSURE_FLAGS))
        .pipe(filter(Boolean))
        .subscribe((pg: PreferenceGroup) => {
          snapshot = {
            ...snapshot,
            preferenceGroups: { ...snapshot.preferenceGroups, hideForeclosureFlags: pg }
          };
          this.save(snapshot);
        })
    );

    this.sub.add(
      this.store$.select(selectPreferenceGroupByCode(PreferenceCode.PG_REPORTS_REPORT_CONFIG))
        .pipe(filter(Boolean))
        .subscribe((pg: PreferenceGroup) => {
          snapshot = {
            ...snapshot,
            preferenceGroups: { ...snapshot.preferenceGroups, reportsReportConfig: pg }
          };
          this.save(snapshot);
        })
    );
  }

  // ── Persistence ───────────────────────────────────────────────────
  private save(snapshot: Partial<SubheaderSnapshot>): void {
    try {
      localStorage.setItem(SUBHEADER_MOCK_STORAGE_KEY, JSON.stringify(snapshot));
    } catch { /* storage full or unavailable */ }
  }

  private loadSnapshot(): SubheaderSnapshot | null {
    try {
      const raw = localStorage.getItem(SUBHEADER_MOCK_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  }

  // ── Console controls (mirrors mockCache pattern) ──────────────────
  private exposeConsoleControls(): void {
    (window as any).subheaderMock = {
      enable: () => {
        localStorage.setItem(SUBHEADER_MOCK_ENABLED_KEY, 'true');
        console.log('[SubheaderMock] ✅ Enabled — refresh to apply');
      },
      disable: () => {
        localStorage.setItem(SUBHEADER_MOCK_ENABLED_KEY, 'false');
        console.log('[SubheaderMock] ❌ Disabled — refresh to apply');
      },
      clear: () => {
        localStorage.removeItem(SUBHEADER_MOCK_STORAGE_KEY);
        console.log('[SubheaderMock] 🗑️ Snapshot cleared');
      },
      status: () => {
        const enabled = isSubheaderMockEnabled();
        const hasData = !!localStorage.getItem(SUBHEADER_MOCK_STORAGE_KEY);
        console.log(`[SubheaderMock] ${enabled ? '✅ Enabled' : '❌ Disabled'} | Snapshot: ${hasData ? 'present' : 'empty'}`);
      },
    };
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
